import { Box, Button, Grid, TextField, Typography, IconButton, MenuItem, Select, FormControl, InputLabel } from '@mui/material';
import CustomContainer from '../../components/CustomContainer';
import PageHeader from '../../components/PageHeader';
import FormSection from '../../components/ui/FormSection';
import ChoiceChip from '../../components/ui/ChoiceChip';
import { FaPaperPlane, FaPlus, FaTrash } from 'react-icons/fa6';
import { useQuery } from '@tanstack/react-query';
import { getCategories } from '../../helpers/httpRequests/product_props';
import { createQuote } from '../../helpers/httpRequests/quotes';
import appToast from '../../components/appToast';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { useModal } from '../../contexts/ModalProvider';
import { metalList } from '../../utils/variables';
import { formatOptions } from '../../utils/functions';

const MAX_IMAGES = 3;

function fileToSquareJpeg(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = () => reject(new Error('Não foi possível ler a imagem.'));
        reader.onload = (event) => {
            const img = new Image();
            img.onerror = () => reject(new Error('Arquivo de imagem inválido.'));
            img.onload = () => {
                const canvas = document.createElement('canvas');
                const size = 500;
                canvas.width = size;
                canvas.height = size;
                const ctx = canvas.getContext('2d');
                const aspectRatio = img.width / img.height;
                let drawWidth;
                let drawHeight;
                let offsetX;
                let offsetY;

                if (aspectRatio > 1) {
                    drawWidth = size * aspectRatio;
                    drawHeight = size;
                    offsetX = -(drawWidth - size) / 2;
                    offsetY = 0;
                } else {
                    drawWidth = size;
                    drawHeight = size / aspectRatio;
                    offsetX = 0;
                    offsetY = -(drawHeight - size) / 2;
                }

                ctx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);
                resolve(canvas.toDataURL('image/jpeg'));
            };
            img.src = event.target.result;
        };
        reader.readAsDataURL(file);
    });
}

export default function QuoteNew() {
    const navigate = useNavigate();
    const { setImage, handleOpen } = useModal();
    const [images, setImages] = useState([]);
    const [selectedMetal, setSelectedMetal] = useState(1);
    const [submitting, setSubmitting] = useState(false);

    const categoriesQuery = useQuery({
        queryKey: ['getCategories'],
        queryFn: getCategories,
        staleTime: 1000 * 60,
    });

    const categories = formatOptions(categoriesQuery.data);

    const handleImageUpload = async (e) => {
        const remaining = MAX_IMAGES - images.length;
        const files = Array.from(e.target.files || []).slice(0, remaining);
        e.target.value = '';
        if (!files.length) return;

        try {
            const next = await Promise.all(files.map(fileToSquareJpeg));
            setImages((prev) => [...prev, ...next].slice(0, MAX_IMAGES));
        } catch (error) {
            appToast.error(error.message || 'Não foi possível incluir a imagem.');
        }
    };

    const handleRemoveImage = (index) => {
        setImages((prev) => prev.filter((_, i) => i !== index));
    };

    const handleCreateQuote = async (e) => {
        e.preventDefault();

        if (images.length < 1) {
            appToast.error('Inclua ao menos uma foto da joia ou do desenho.');
            return;
        }

        const formData = new FormData(e.currentTarget);
        const payload = {
            name: formData.get('name'),
            category_id: formData.get('category'),
            selected_metal: selectedMetal,
            description: formData.get('description'),
            images,
        };

        setSubmitting(true);
        try {
            await createQuote(payload);
            navigate('/orcamento-resultado/success');
        } catch (error) {
            appToast.error(error.message || 'Erro ao criar orçamento');
            setSubmitting(false);
        }
    };

    return (
        <CustomContainer>
            <Box component="form" onSubmit={handleCreateQuote} sx={{ my: 4 }}>
                <PageHeader
                    title="Pedir orçamento"
                    crumbs={[{ label: 'Meus orçamentos', to: '/orcamentos' }, 'Pedir orçamento']}
                />

                <FormSection title="Fotos">
                    <Typography sx={styles.lede}>
                        Até {MAX_IMAGES} fotos. Use referência da peça, desenho ou detalhe que a oficina precisa ver.
                    </Typography>
                    <Box sx={styles.gallery}>
                        {images.map((src, index) => (
                            <Box key={`${index}-${src.slice(-12)}`} sx={styles.thumbWrap}>
                                <IconButton
                                    size="small"
                                    aria-label={`Remover foto ${index + 1}`}
                                    onClick={() => handleRemoveImage(index)}
                                    sx={styles.deleteButton}
                                >
                                    <FaTrash size={12} />
                                </IconButton>
                                <Box
                                    component="button"
                                    type="button"
                                    onClick={() => {
                                        setImage(src);
                                        handleOpen();
                                    }}
                                    sx={styles.thumbBtn}
                                >
                                    <Box
                                        component="img"
                                        src={src}
                                        alt={`Foto ${index + 1} do orçamento`}
                                        sx={styles.thumb}
                                    />
                                </Box>
                            </Box>
                        ))}
                        {images.length < MAX_IMAGES && (
                            <Button component="label" sx={styles.addTile}>
                                <FaPlus size={16} />
                                <span>Incluir foto</span>
                                <input
                                    type="file"
                                    accept="image/*"
                                    multiple
                                    hidden
                                    onChange={handleImageUpload}
                                />
                            </Button>
                        )}
                    </Box>
                    <Typography sx={styles.count}>
                        {images.length} de {MAX_IMAGES}
                    </Typography>
                </FormSection>

                <FormSection title="A joia">
                    <Grid container spacing={3}>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                                required
                                label="Nome"
                                fullWidth
                                name="name"
                                helperText="Como você chama essa peça."
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <FormControl required fullWidth>
                                <InputLabel>Categoria</InputLabel>
                                <Select required fullWidth label="Categoria" name="category" defaultValue="">
                                    <MenuItem value="" disabled>Selecionar</MenuItem>
                                    {categories.map((opt) => (
                                        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid size={12}>
                            <Typography sx={styles.fieldLabel}>Material</Typography>
                            <Box sx={styles.metals}>
                                {metalList.map((material) => (
                                    <ChoiceChip
                                        key={material.value}
                                        selected={selectedMetal === material.value}
                                        onClick={() => setSelectedMetal(material.value)}
                                    >
                                        {material.label}
                                    </ChoiceChip>
                                ))}
                            </Box>
                        </Grid>
                        <Grid size={12}>
                            <TextField
                                multiline
                                required
                                rows={6}
                                label="Descrição"
                                fullWidth
                                name="description"
                                helperText="Medidas, gravura, pedras ou o que a peça precisa ter."
                            />
                        </Grid>
                    </Grid>
                </FormSection>

                <Box sx={styles.submit}>
                    <Button
                        startIcon={<FaPaperPlane size={14} />}
                        variant="contained"
                        color="secondary"
                        type="submit"
                        disabled={submitting}
                    >
                        {submitting ? 'Enviando…' : 'Pedir orçamento'}
                    </Button>
                </Box>
            </Box>
        </CustomContainer>
    );
}

const styles = {
    lede: {
        mb: 2.5,
        color: 'text.secondary',
        textAlign: 'left',
        maxWidth: 520,
    },
    gallery: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 1.5,
    },
    thumbWrap: {
        position: 'relative',
        width: 120,
        height: 120,
    },
    deleteButton: {
        position: 'absolute',
        top: 6,
        right: 6,
        zIndex: 1,
        width: 32,
        height: 32,
        bgcolor: 'background.paper',
        color: 'text.primary',
        border: '1px solid',
        borderColor: 'divider',
        '&:hover': {
            bgcolor: 'background.paper',
            color: 'error.main',
        },
    },
    thumbBtn: {
        p: 0,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        overflow: 'hidden',
        bgcolor: 'background.default',
        cursor: 'pointer',
        '&:focus-visible': {
            outline: '2px solid',
            outlineColor: 'secondary.main',
            outlineOffset: 2,
        },
    },
    thumb: {
        display: 'block',
        width: 120,
        height: 120,
        objectFit: 'cover',
        aspectRatio: '1 / 1',
    },
    addTile: {
        width: 120,
        height: 120,
        minWidth: 120,
        flexDirection: 'column',
        gap: 0.75,
        border: '1px dashed',
        borderColor: 'divider',
        borderRadius: 1,
        color: 'text.secondary',
        bgcolor: 'background.default',
        fontSize: '0.75rem',
        fontWeight: 600,
        '&:hover': {
            borderColor: 'secondary.main',
            bgcolor: 'background.default',
            color: 'text.primary',
        },
    },
    count: {
        mt: 1.5,
        fontSize: '0.75rem',
        fontVariantNumeric: 'tabular-nums',
        color: 'text.secondary',
    },
    fieldLabel: {
        fontSize: '0.6875rem',
        fontWeight: 600,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: 'text.secondary',
        mb: 1,
        textAlign: 'left',
    },
    metals: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 1,
    },
    submit: {
        display: 'flex',
        justifyContent: 'flex-end',
    },
};
